import { Title } from '@solidjs/meta'
import { type Component, createResource, createSignal, For, Show } from 'solid-js'
import Button from '~/components/Button'
import Container from '~/components/Container'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Select from '~/components/Select'
import Textarea from '~/components/Textarea'
import { useToast } from '~/components/useToast'
import { workflowCredentialService } from '~/services/workflow.service'
import type { WorkflowCredentialType } from '~/types/workflow'
import { resolveError } from '~/utils/error'

const CredencialesPage: Component = () => {
  const toast = useToast()
  const [creds, { refetch }] = createResource<WorkflowCredentialType[]>(() => workflowCredentialService.list().catch(() => []))
  const [show, setShow] = createSignal(false)
  const [name, setName] = createSignal('')
  const [type, setType] = createSignal('bearer')
  const [dataJson, setDataJson] = createSignal('{}')

  const handleCreate = async () => {
    let data: unknown
    try {
      data = JSON.parse(dataJson())
    } catch {
      toast.addToast({ title: 'JSON inválido', type: 'danger' })
      return
    }
    if (typeof data !== 'object' || data === null) {
      toast.addToast({ title: 'El JSON debe ser un objeto', type: 'danger' })
      return
    }
    // JSON.parse devuelve `any`; tras validar que es objeto, se acota al tipo esperado.
    const payload = data as Record<string, unknown>
    try {
      await workflowCredentialService.create({ name: name(), type: type(), data: payload })
      toast.addToast({ title: 'Credencial creada', type: 'success' })
      setShow(false)
      setName('')
      setDataJson('{}')
      void refetch()
    } catch (e) {
      toast.addToast({ title: resolveError(e), type: 'danger' })
    }
  }

  return (
    <>
      <Title>Credenciales | Automatizaciones</Title>
      <Container>
        <div class="flex items-center justify-between mb-4">
          <h1 class="text-xl font-bold">Credenciales de Workflows</h1>
          <Button color="primary" icon="material-symbols-light:add" onClick={() => setShow(true)}>
            Nueva
          </Button>
        </div>
        <div class="grid gap-2">
          <For each={creds() ?? []}>
            {c => (
              <div class="rounded-lg border border-border p-3 flex items-center justify-between">
                <div>
                  <p class="font-medium text-sm">{c.name}</p>
                  <p class="text-xs text-foreground-subtle">
                    {c.type} · {c.hint ?? ''}
                  </p>
                </div>
                <Button
                  size="xs"
                  variant="text"
                  color="danger"
                  icon="material-symbols-light:delete-outline"
                  onClick={async () => {
                    await workflowCredentialService.delete(c.id)
                    void refetch()
                  }}
                />
              </div>
            )}
          </For>
          <Show when={!creds()?.length}>
            <p class="text-sm text-foreground-subtle">Sin credenciales. Guarda tokens aquí y referéncialos como credentialId en nodos HTTP.</p>
          </Show>
        </div>
      </Container>
      <Modal isOpen={show()} onClose={() => setShow(false)} size="md">
        <Modal.Header>
          <h2 class="font-semibold">Nueva credencial</h2>
        </Modal.Header>
        <Modal.Body>
          <div class="space-y-3">
            <Input label="Nombre" value={name()} onInput={e => setName(e.currentTarget.value)} placeholder="mi-api" />
            <Select
              label="Tipo"
              value={type()}
              onChange={v => setType(String(v))}
              options={[
                { value: 'bearer', title: 'Bearer' },
                { value: 'header', title: 'Header' },
                { value: 'basic', title: 'Basic' },
                { value: 'hmac', title: 'HMAC' }
              ]}
            />
            <Textarea label="Datos JSON" value={dataJson()} onInput={e => setDataJson(e.currentTarget.value)} rows={4} placeholder='{"token":"..."} o {"name":"X-Api-Key","value":"..."}' />
            <p class="text-xs text-foreground-subtle">Se cifra con AES-GCM; en el grafo sólo queda credentialId.</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button variant="soft" onClick={() => setShow(false)}>
              Cancelar
            </Button>
            <Button color="primary" onClick={handleCreate}>
              Crear
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default CredencialesPage
